/**
 * Téléchargement des documents figés par le serveur.
 *
 * Les aperçus dessinés dans la page servent à relire une pièce avant de
 * l'envoyer ; le document qui fait foi est celui que le serveur produit et
 * conserve. C'est lui que l'on ouvre ici, pour que le client reçoive et que
 * l'entreprise archive exactement la même pièce.
 */

import { ouvreVisionneuse } from '../components/Visionneuse.jsx';
import { ApiError, adresseApi } from './client.js';
import { tr } from '../lib/i18n';

/** Attente maximale d'un document préparé par le serveur. */
const DELAI_DOCUMENT_MS = 90_000;

/**
 * Ouvre un document produit par l'API.
 *
 * <p>Le fichier est récupéré par `fetch` plutôt que par un simple lien : le
 * serveur refuse d'émettre certaines pièces — un brouillon, par exemple — et un
 * lien afficherait alors le message d'erreur brut à la place du document.
 *
 * <p>Un document dont le contenu est écrit par l'utilisateur — le certificat de
 * travail et son appréciation, la lettre de résiliation et ses dates — se
 * demande avec un corps, et donc en POST : ce corps est le document lui-même.
 *
 * @param {string} chemin chemin du document sous `/api`
 * @param {object} [params] paramètres de requête
 * @param {object} [corps] contenu du document ; la requête passe alors en POST
 * @returns {Promise<void>} rien ; le document s'ouvre dans un nouvel onglet
 * @throws {ApiError} si le serveur refuse de produire le document
 */
export async function ouvreDocument(chemin, params, corps) {
  const preparation = (async () => {
    // Un serveur qui ne répond pas laisse un message, pas une attente sans fin.
    const controle = typeof AbortController === 'undefined' ? null : new AbortController();
    const minuteur = controle ? setTimeout(() => controle.abort(), DELAI_DOCUMENT_MS) : null;
    try {
      const reponse = await fetch(adresseDocument(chemin, params), {
        method: corps === undefined ? 'GET' : 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/pdf',
          ...(corps === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
        ...(controle ? { signal: controle.signal } : {}),
      });
      await verifie(reponse);
      const blob = await reponse.blob();
      return { blob, titre: nomDeFichier(reponse.headers?.get?.('content-disposition')) || '' };
    } catch (e) {
      if (e?.name === 'AbortError') {
        throw new ApiError(tr('Le PDF met trop de temps à arriver. Réessaie dans un instant.'), {
          status: 0,
        });
      }
      throw e;
    } finally {
      if (minuteur) clearTimeout(minuteur);
    }
  })();
  // Dans SwissTech, le document s'ouvre dans la visionneuse, avec son bouton
  // « Retour » : un onglet, dans l'application installée, n'en a pas. Elle
  // s'ouvre tout de suite, « Préparation du PDF… » à l'écran, et montre le
  // document ou le message d'erreur dès que le serveur répond.
  if (ouvreVisionneuse({ promesse: preparation })) {
    // L'erreur est déjà à l'écran, dans la visionneuse ; elle remonte quand
    // même à l'écran appelant, qui sinon annonçait « ✓ Lettre remise » ou
    // « PDF ouvert » alors que le serveur avait refusé.
    await preparation.catch((e) => {
      if (e && typeof e === 'object') e.dejaAffiche = true;
      throw e;
    });
    return;
  }
  const { blob } = await preparation;
  const adresse = URL.createObjectURL(blob);
  window.open(adresse, '_blank', 'noopener');
  // L'onglet a pris le contenu : l'adresse temporaire peut être rendue. La
  // libérer trop tôt laisserait une page blanche sur les navigateurs lents.
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
}

/**
 * Télécharge un fichier produit par l'API, sous le nom que le serveur lui donne.
 *
 * <p>Un CSV ou un XML ne se lit pas dans un onglet : le navigateur le
 * rendrait en texte brut. Il s'enregistre, pour être remis à la fiduciaire ou
 * ouvert dans un tableur.
 *
 * @param {string} chemin chemin du fichier sous `/api`
 * @param {object} [params] paramètres de requête
 * @returns {Promise<string>} le nom du fichier enregistré
 * @throws {ApiError} si le serveur refuse de produire le fichier
 */
export async function telechargeFichier(chemin, params) {
  const reponse = await fetch(adresseDocument(chemin, params), {
    method: 'GET',
    credentials: 'include',
  });
  await verifie(reponse);

  const nom = nomDeFichier(reponse.headers.get('content-disposition')) || 'export';
  const adresse = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = adresse;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
  return nom;
}

/**
 * Récupère un document produit par l'API comme fichier, pour le partager.
 *
 * <p>Le partage du téléphone (WhatsApp, Mail, AirDrop…) joint le fichier lui-
 * même : le client reçoit le PDF, pas seulement un message.
 *
 * @param {string} chemin chemin du document sous `/api`
 * @returns {Promise<File>} le PDF, sous le nom que le serveur lui donne
 * @throws {ApiError} si le serveur refuse de produire le document
 */
export async function fichierDocument(chemin) {
  const reponse = await fetch(adresseDocument(chemin), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/pdf' },
  });
  await verifie(reponse);
  const nom = nomDeFichier(reponse.headers.get('content-disposition')) || 'document.pdf';
  return new File([await reponse.blob()], nom, { type: 'application/pdf' });
}

/**
 * Un document dont le contenu part avec la demande (POST), récupéré comme
 * fichier : pour l'enregistrer ou le partager sans passer par la visionneuse.
 *
 * @param {string} chemin chemin du document sous `/api`
 * @param {object} corps contenu du document
 * @param {string} [nomParDefaut] nom du fichier si le serveur n'en donne pas
 * @returns {Promise<File>} le PDF
 * @throws {ApiError} si le serveur refuse de produire le document
 */
export async function fichierDocumentDemande(chemin, corps, nomParDefaut = 'document.pdf') {
  const reponse = await fetch(adresseDocument(chemin), {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/pdf', 'Content-Type': 'application/json' },
    body: JSON.stringify(corps),
  });
  await verifie(reponse);
  const nom = nomDeFichier(reponse.headers.get('content-disposition')) || nomParDefaut;
  return new File([await reponse.blob()], nom, { type: 'application/pdf' });
}

/**
 * Enregistre un fichier sur l'appareil, sous son nom.
 *
 * @param {File} fichier le fichier
 * @returns {void}
 */
export function enregistreFichier(fichier) {
  const adresse = URL.createObjectURL(fichier);
  const lien = document.createElement('a');
  lien.href = adresse;
  lien.download = fichier.name;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
}

/** Documents en cours de préparation ou prêts, par adresse et version. */
const preparations = new Map();
const DUREE_CACHE_MS = 10 * 60 * 1000;
const attente = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Le PDF officiel, préparé une seule fois et repris s'il échoue.
 *
 * <p>La première préparation d'un document peut être longue : le serveur le
 * met en page puis le garde. Si le téléphone abandonnait l'attente avant la
 * fin, le bouton d'envoi disparaissait, et ne revenait qu'en rouvrant le menu
 * quelques minutes plus tard, quand le serveur avait fini. La demande est
 * maintenant reprise d'elle-même ; une préparation déjà lancée (à l'ouverture
 * de la pièce) est reprise telle quelle au lieu d'être recommencée.
 *
 * @param {string} chemin chemin du document sous `/api`
 * @param {string} [version] contenu de la pièce : une pièce modifiée se prépare à nouveau
 * @param {{essais?: number, pause?: number}} [options] reprises en cas d'échec
 * @returns {Promise<File>} le fichier
 */
export function fichierDocumentPrepare(chemin, version = '', options = {}) {
  // Sans version, pas de mémoire : une pièce modifiée renverrait l'ancien PDF.
  const cle = version ? chemin + '#' + version : null;
  const deja = cle && preparations.get(cle);
  if (deja && Date.now() - deja.quand < DUREE_CACHE_MS) return deja.promesse;
  const essais = options.essais ?? 4;
  const pause = options.pause ?? 2500;
  const promesse = (async () => {
    let derniere;
    for (let i = 0; i < essais; i++) {
      try {
        return await fichierDocument(chemin);
      } catch (e) {
        derniere = e;
        // Un refus du serveur (pièce introuvable, brouillon) ne se reprend pas.
        const statut = e?.status;
        if (statut && statut < 500 && statut !== 408 && statut !== 429) break;
        if (i < essais - 1) await attente(pause * (i + 1));
      }
    }
    throw derniere;
  })();
  if (cle) {
    preparations.set(cle, { promesse, quand: Date.now() });
    promesse.catch(() => preparations.delete(cle));
  }
  return promesse;
}

/**
 * Lit le nom de fichier d'un en-tête `Content-Disposition`.
 *
 * @param {string|null} disposition valeur de l'en-tête
 * @returns {string|null} le nom, ou `null` s'il n'y en a pas
 */
export function nomDeFichier(disposition) {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || '');
  return m ? decodeURIComponent(m[1].trim()) : null;
}

/** Adresse complète d'un document, paramètres vides omis. */
function adresseDocument(chemin, params) {
  const query = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(params || {})) {
    if (valeur !== undefined && valeur !== null && valeur !== '') query.append(cle, String(valeur));
  }
  const suffixe = query.toString();
  return adresseApi(`/api${chemin}`) + (suffixe ? `?${suffixe}` : '');
}

/** Traduit un refus du serveur en erreur lisible, à partir du corps problem+json. */
async function verifie(reponse) {
  if (reponse.ok) return;
  let probleme = null;
  try {
    probleme = await reponse.json();
  } catch {
    // Réponse sans corps JSON : on se rabat sur le statut.
  }
  throw new ApiError(
    probleme?.detail ||
      probleme?.title ||
      `Le document n'a pas pu être produit (HTTP ${reponse.status}).`,
    { status: reponse.status, title: probleme?.title },
  );
}
